import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Input, Modal, Space, Table } from 'antd';
// Api
import { GET_BRAND_RESTRICTED_CATEGORIES } from 'api/brandRestrictedCategory/queries';
// Types
import {
  GetBrandRestrictedCategories,
  GetBrandRestrictedCategories_getBrandRestrictedCategories,
} from 'api/brandRestrictedCategory/types/GetBrandRestrictedCategories';
// Components
import CreateEditBrandRestrictedCategory from './components/CreateEditBrandRestrictedCategory/CreateEditBrandRestrictedCategory';
import DeleteBrandRestrictedCategoryModal from './components/DeleteBrandRestrictedCategoryModal/DeleteBrandRestrictedCategoryModal';
// Styles
import styles from './ManageBrandRestrictedCategories.module.scss';

type BrandRestrictedCategory =
  GetBrandRestrictedCategories_getBrandRestrictedCategories;

const ManageBrandRestrictedCategories = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRecord, setSelectedRecord] =
    useState<BrandRestrictedCategory | null>(null);
  const [recordToDelete, setRecordToDelete] =
    useState<BrandRestrictedCategory | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] =
    useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);

  const { data, loading } = useQuery<GetBrandRestrictedCategories>(
    GET_BRAND_RESTRICTED_CATEGORIES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const brandRestrictedCategories = data?.getBrandRestrictedCategories;
  const categories = useMemo(
    () => brandRestrictedCategories || [],
    [brandRestrictedCategories]
  );

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return categories;
    }

    return categories.filter((category) =>
      category.name.toLowerCase().includes(term)
    );
  }, [categories, searchTerm]);

  const openCreateModal = () => {
    setSelectedRecord(null);
    setIsCreateModalVisible(true);
  };

  const openEditModal = (record: BrandRestrictedCategory) => {
    setSelectedRecord(record);
    setIsEditModalVisible(true);
  };

  const hideCreateEditModal = () => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setSelectedRecord(null);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 320,
      align: 'center' as const,
      onCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: BrandRestrictedCategory, b: BrandRestrictedCategory) =>
        a.name.localeCompare(b.name),
    },
    {
      title: 'Used by brands',
      dataIndex: 'usedByBrandsCount',
      key: 'usedByBrandsCount',
      width: 150,
      align: 'center' as const,
      sorter: (a: BrandRestrictedCategory, b: BrandRestrictedCategory) =>
        a.usedByBrandsCount - b.usedByBrandsCount,
    },
    {
      title: 'Used by talents',
      dataIndex: 'usedByTalentsCount',
      key: 'usedByTalentsCount',
      width: 150,
      align: 'center' as const,
      sorter: (a: BrandRestrictedCategory, b: BrandRestrictedCategory) =>
        a.usedByTalentsCount - b.usedByTalentsCount,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 200,
      render: function Actions(_: unknown, record: BrandRestrictedCategory) {
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => openEditModal(record)}>
              Edit
            </Button>
            <Button danger onClick={() => setRecordToDelete(record)}>
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const isEditing = !!selectedRecord && isEditModalVisible;
  const isModalVisible = isCreateModalVisible || isEditing;

  return (
    <div className={styles.root}>
      <PageToolbar
        leading={
          <Input.Search
            placeholder="Search by name"
            allowClear
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      >
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={openCreateModal}
        >
          Add restricted category
        </Button>
      </PageToolbar>

      <Table<BrandRestrictedCategory>
        rowKey="id"
        columns={columns}
        dataSource={filteredCategories}
        loading={loading}
        pagination={false}
        scroll={{ x: 1050 }}
      />

      <Modal
        title={
          isEditing ? 'Edit restricted category' : 'New restricted category'
        }
        visible={isModalVisible}
        onCancel={hideCreateEditModal}
        footer={null}
        destroyOnClose
      >
        <CreateEditBrandRestrictedCategory
          record={selectedRecord}
          onFinish={hideCreateEditModal}
        />
      </Modal>

      <DeleteBrandRestrictedCategoryModal
        record={recordToDelete}
        categories={categories}
        onClose={() => setRecordToDelete(null)}
      />
    </div>
  );
};

export default ManageBrandRestrictedCategories;
